import AppModal from '@/components/AppModal';
import AppLoadingButton from '@/components/mui/LoadingButton';
import API from '@/constant/API';
import useToggle from '@/hook/useToggle';
import axiosInstance from '@/utils/axios';
import { Box, Stack, Typography, Checkbox, RadioGroup, FormControlLabel, Radio, FormControl } from '@mui/material';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import SignatureDrawing from './components/SignatureDrawing';
import RpIcon from '@/components/RpIcon';
import * as Yup from "yup";
import { dispatch } from '@/redux/store';
import { getUserInfo } from '@/redux/slices/user';
import AppTextField from '@/components/AppTextField';
import { enqueueSnackbar } from 'notistack';
import classes from "./css/index.module.less";
import SimpleHeader from "@/pages/components/Headers/SimpleHeader";
import useWindowSize from '@/hook/useWindowSize';
import { Modal } from '@/components/mui';

function KycAml() {
  const { t } = useTranslation();
  const [conf, setConf] = useState(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { width } = useWindowSize();
  const [drawerWidth, setDrawerWidth] = useState(300);

  useEffect(() => {
    setDrawerWidth(width > 300 ? 300 : width);
  }, [width]);
  
  const [signatureBase64, setSignatureBase64] = useState(null);
  const {
    toggle: signatureShow,
    onToggle: onToggleSignatureShow,
  } = useToggle(false);

  const {
    toggle: checkContentShow,
    onToggle: onToggleCheckContentShow,
  } = useToggle(false);

  const [fields, setFields] = useState([]);
  const [inputFields, setInputFields] = useState([]);
  const [contentChecked, setContentChecked] = useState(false);

  useEffect(() => {
    _loadConf();
  }, []);

  const _loadConf = async () => {
    const res = await axiosInstance.post(API.KYC_DECLARATION_FORM_GET);
    setConf(res);
  }

  useEffect(() => {
    if (conf && conf.items?.length) {
      _updateFields();
    }
  }, [conf]);

  const _updateFields = () => {
    const fields = [];
    conf.items.forEach((item) => {
      const field = { ...item };
      // init field value
      field.value = {
        check: null,
        detail: null,
      }
      fields.push(field);
    });
    setFields(fields);
  }

  const _onSave = async () => {
    if (!signatureBase64) {
      enqueueSnackbar(t('signature_required'), { variant: 'error' });
      return;
    }
    try {
      _getSubmitItems();
      onToggleCheckContentShow();
    } catch(e) {
      enqueueSnackbar(e.message, { variant: 'error' });
    }
  };

  const _getSubmitItems = () => {
    const items = [];
    fields.forEach((field) => {
      if (!field.value.check) {
        throw new Error('Form is not validate for save');
      }
      
      const item = {
        check: field.value.check,
      };
      if (field.detailRequired == field.value.check) {
        if (!values[field.questionId]) {
          throw new Error('Form is not validate for save');
        }
        item['detail'] = values[field.questionId];
      }
      items.push(item);
    });
    return items;
  }

  const _onConfirmSave = async () => {
    onToggleCheckContentShow();

    setLoading(true);
    try {
      await axiosInstance.post(API.KYC_DECLARATION_FORM_SUBMIT, {
        signatureBase64: signatureBase64,
        items: _getSubmitItems(),
      });
      // TODO: toast提示提交aml证明成功
      enqueueSnackbar(t('successful'), { variant: 'success' })
      dispatch(getUserInfo());
      navigate(-1);
    } finally {
      setLoading(false);
    }
  }

  const _onCheckBoxChange = (field, val) => {
    field.value.check = val;

    const idx = fields.findIndex(item => item.questionId === field.questionId);
    fields[idx] = field;
    setFields(fields.concat([]));
  }

  const _createFormItems = () => {
    if (!fields.length) return null;
    
    return fields.map((field, index) => {
      const showInput = field.value.check == field.detailRequired;
      return <Box key={index}>
        <div className={classes['aml-title']}>{field.title}</div>
        <FormControl>
          <RadioGroup
            value={field.value.check}
            onChange={(evt) => _onCheckBoxChange(field, evt.target.value)}
          >
            {field.checkOptions.map((opt, index) =>
              <FormControlLabel key={index} value={opt} control={<Radio />} label={opt} />
            )}
          </RadioGroup>
        </FormControl>

        {/* todo 仅可输入英文 */}
        {showInput && <AppTextField
          fullWidth
          autoComplete="off"
          name={field.questionId}
          size="large"
          clearable
          required
          variant="outlined"
          onChange={(e) => {
            handleChange(e);
          }}
          onBlur={handleBlur}
          onClear={() => setFieldValue(field.questionId, "")}
          error={Boolean(touched[field.questionId] && errors[field.questionId])}
          helperText={touched[field.questionId] && t(errors[field.questionid])}
          value={values[field.questionId]}
        />}
      </Box>
    });
  }

  const _onSignature = (evt) => {
    if (evt.target.className?.indexOf('iconfont') != -1) {
      _onClearSignature(evt);
      return;
    }
    onToggleSignatureShow();
  }

  const _onClearSignature = (evt) => {
    evt.stopPropagation();
    setSignatureBase64(null);
  }

  const _createSignaturePreview = () => {
    if (!conf.requireSignature) return null;

    if (!signatureBase64) {
      return <Box onClick={_onSignature} className={classes['signature']}>
        <RpIcon icon={'edit'}/>
        {t('click_to_add_signature')}
      </Box>
    }

    return <Stack direction={'row'} alignItems={'center'} justifyContent={'center'} onClick={_onSignature} className={classes['signature']}>
      <img src={signatureBase64} height={100} />
      <RpIcon icon={'trash'} onClick={_onClearSignature} />
    </Stack>
  }

  const _onSaveSignature = (base64) => {
    setSignatureBase64(base64);
    onToggleSignatureShow();
  }

  const initialValues = {};
  let i = 0;
  const yupShape = {};
  fields.forEach((_) => {
    const fieldName = `field${i}`;
    initialValues[fieldName] = '';
  });

  inputFields.forEach((inputField) => {
    yupShape[inputField] = Yup.string().trim().required(t("this_field_is_required"));
  });

  const {
    errors,
    values,
    touched,
    setFieldValue,
    handleChange,
    handleBlur,
    handleSubmit,
  } = useFormik({
    initialValues: initialValues,
    validationSchema: Yup.object().shape(yupShape),
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: _onSave,
  });

  if (!conf) return;
  
  return (<Box>
    <SimpleHeader />
    <Stack direction={'column'} gap={'32px'} className='container-wrap'>
      {!!conf.formTitle && <Box className='h2'>{conf.formTitle}</Box>}

      <form autoComplete="off" onSubmit={handleSubmit}>
        <Stack direction={'column'} gap={'32px'}>
          {_createFormItems()}
        </Stack>
        {_createSignaturePreview()}

        <AppLoadingButton
          loading={loading}
          variant="contained"
          fullWidth
          size="large"
          type="submit"
        >
          {t("submit")}
        </AppLoadingButton>
      </form>
    </Stack>
    
    <Modal width={drawerWidth} open={signatureShow} onClose={onToggleSignatureShow}>
      <SignatureDrawing width={drawerWidth} onSave={_onSaveSignature} />
    </Modal>

    <AppModal open={checkContentShow} onClose={onToggleCheckContentShow}>
      <Stack direction={'row'} alignItems={'start'} sx={{ padding: '16px' }}>
        <Checkbox checked={contentChecked} onChange={(e) => setContentChecked(e.target.checked)} />
        {!!conf.checkContent && <Typography>{conf.checkContent}</Typography>}
      </Stack>

      <AppLoadingButton disabled={!contentChecked} variant='contained' fullWidth onClick={_onConfirmSave}>
        {t('confirm')}
      </AppLoadingButton>
    </AppModal>
  </Box>)
}

export default KycAml